/**
 * NOTE on redux: this mirrors mobile's data sourcing pattern.
 *
 * The business record shown here (top section, cover image, coupon cards'
 * `business` field) comes SOLELY from `affiliateBusiness.getAffiliateBusiness(id)`
 * -> `state.affiliateBusiness.data`. `businesses/details` is deliberately NOT
 * used for display data — that endpoint is self-scoped (it returns whichever
 * business the logged-in session belongs to, ignoring the `id` in the URL).
 *
 * Dropped (mobile-specific, no clean web equivalent):
 *  - AsyncStorage caching of followed businesses / favourites for offline-ish
 *    instant paint. On web this isn't needed since redux + the network are
 *    fast enough on first load; localStorage caching could be added later if
 *    actually needed.
 *  - FCM push token registration (saveFcmToken via @react-native-firebase/
 *    messaging) — push notifications are a separate concern from this
 *    screen and have no direct web equivalent here.
 *  - perf/InteractionManager instrumentation (T0-T5 console.log timing
 *    marks) — RN-specific profiling, not meaningful on web.
 *  - useFeedImpressions/trackClick dwell-time analytics — same reasoning as
 *    CouponsListAll's port.
 *
 * Redux: uses customerCoupons.all/favourite/applied/categories/followedCoupons
 * (not coupons.*, which is the business-CRUD module) and businesses.all/follow,
 * matching every other customer-facing screen converted so far. followedCoupons
 * is a new small module built specifically to support this screen — see
 * coupon-common/src/modules/customerCoupons/followedCoupons.
 *
 * SPONSORED CARD — SponsoredCard (a static promo for MyCouponbook's own
 * "list your business" signup, not a real coupon) is inserted at a fixed
 * position in the Newest Coupons grid: right after the 9th real coupon
 * (index === 8, 0-based), making it the 10th card overall. It renders
 * exactly once regardless of pagination, since the .map() index check can
 * only match a single iteration. See SponsoredCard.js for the component
 * itself.
 *
 * DESIGN UPDATE: markup/styling only was ported to match the new marketing
 * site's visual language (Poppins, red-500→red-900 gradient accents,
 * clamp()-based fluid sizing, rounded-full pills, arrow-button carousels).
 * Still built on react-css-modules/SCSS like the rest of this codebase —
 * see Home.module.scss for the new design tokens. No data, redux wiring,
 * handlers, or business logic were touched.
 *
 * CITY PICKER WIRING — the hero "Select Location" field used to be a bare,
 * unwired <input> writing into local `heroLocation` state that nothing ever
 * read. It's now the same CityPickerButtonModal used on CouponsListAll,
 * passed the `autoConfirm` prop so multiple cities can be picked and each
 * pick commits immediately (no separate confirm/search button needed) —
 * the confirmLabel="Done" button is only there to close the popover once
 * they're finished, not to commit the selection:
 *  - `activeCityId` is restored to the real redux selector (it had been
 *    hardcoded to 347 for local debugging — that hardcode is removed).
 *  - Picking cities from THIS hero dropdown only updates local
 *    `selectedCities` state (drives heroLocationLabel and gets passed as
 *    preSelectedCities to CouponsListAll on navigation) — it deliberately
 *    does NOT dispatch cityActions.setActiveCity and does NOT touch
 *    localStorage's CACHE_KEYS.DEFAULT_CITY. Site-wide activeCityId (which
 *    drives this screen's own getAllList/isReady/etc.) is only ever changed
 *    from the header's city picker, never from here.
 *  - The trigger is restyled (via .hero-city-picker in Home.module.scss)
 *    to match the glass .hero-select-trigger "Category" pill next to it,
 *    overriding CityPickerButtonModal's own default 'chip' variant.
 *
 * HERO CATEGORY DROPDOWN — the hero "Category" field was a native <select>,
 * which can't be restyled beyond its trigger (the open dropdown list itself
 * always renders as the browser/OS default). Replaced with the same custom
 * button + portaled dropdown list pattern already used for the Category
 * chip on CouponsListAll (see HeroDropdownPortal below — a copy of
 * CouponsListAll's DropdownPortal, since that one isn't exported from a
 * shared module). Selection/search-on-click behavior is unchanged.
 *
 * CATEGORY ICON IMPORT PATHS — these live two levels up from this file
 * (src/screens/Home/index.js -> src/assets/images/...), same base path as
 * the existing CoffeeShopBG/NoImage imports below. An earlier version of
 * this file mistakenly used three `../` levels, which resolved outside
 * src/ entirely and broke the build (CRA disallows imports outside src/).
 * If your icon files don't actually live under an images/categories/
 * subfolder, adjust the path segment accordingly.
 *
 * GUEST REDIRECT — logged-out visitors landing on this route are bounced
 * to /guest-home instead of seeing (and firing all the authenticated
 * fetches for) the full Home screen. Gated on auth.persist.restore.fetching
 * so a real logged-in user isn't bounced during the brief redux-persist
 * token-rehydration window on page load — see the guard right after the
 * redux selects below. ASSUMPTION: the exact path
 * `state.auth.persist.restore.fetching` is inferred from the console dump
 * you shared (`persist: {save, restore, forgot}`); confirm the field name
 * matches and adjust if not. Also using the literal '/guest-home' path
 * since that's what showed in your devtools screenshot — swap for a
 * RouteNames constant if/when you add one.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import cx from 'classnames';
import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { ChevronDown, ChevronUp, Grid, Heart } from 'react-feather';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CityPickerButtonModal, CustomerCouponCard, CustomerHeader, Spinner, SeoHead } from 'components';
import { COUPON_TYPES } from 'coupon-common';
import { actions as businessActions } from 'coupon-common/src/modules/businesses/all';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';
import { actions as cityActions } from 'coupon-common/src/modules/cities';
import { actions } from 'coupon-common/src/modules/customerCoupons/all';
import { actions as appliedActions } from 'coupon-common/src/modules/customerCoupons/applied';
import { actions as categoryActions } from 'coupon-common/src/modules/customerCoupons/categories';
import { actions as favCouponActions } from 'coupon-common/src/modules/customerCoupons/favourite';
import { actions as followedCouponsActions } from 'coupon-common/src/modules/customerCoupons/followedCoupons';
import { actions as customerProfileActions } from 'coupon-common/src/modules/customerProfile';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import { Helmet } from 'react-helmet';
import foodDrinkIcon from '../../assets/images/categories/CategoryFoodDrink.webp';
import selfCareIcon from '../../assets/images/categories/CategorySelfCare.webp';
import servicesIcon from '../../assets/images/categories/CategoryServices.webp';
import shoppingIcon from '../../assets/images/categories/CategoryShopping.webp';
import thingsToDoIcon from '../../assets/images/categories/CategoryThingsToDo.webp';
import CoffeeShopBG from '../../assets/images/CoffeeShopBG.webp';
import NoImage from '../../assets/images/NoCouponImage.webp';
import LoadingLogo from '../../components/LoadingLogo/LoadingLogo';
import SponsoredCard from '../../components/SponsoredCard/SponsoredCard'; // adjust path to match your actual components/ import pattern
import { RouteNames } from '../../constants';
import styles from './Home.module.scss';
import { FavoritesSkeleton, FollowedBusinessesSkeleton, NewestCouponsSkeleton } from './SkeletonComponents';
import TrackedCouponCard from '../../components/TrackedCouponCard/TrackedCouponCard'; // adjust to actual path
import { useFeedCardTracking } from '../../utils/useFeedCardTracking'; // adjust path

const CACHE_KEYS = {
  DEFAULT_CITY: 'DEFAULT_CITY',
};

/**
 * SESSION-SCOPED FETCH CACHE — plain module-scope variable, NOT
 * sessionStorage/localStorage. See GuestHome/index.js for the full
 * rationale (identical pattern, ported here 1:1). Tracks only the city we
 * last fetched coupons for; the coupons themselves already live in redux
 * and survive navigation on their own once this effect stops wiping them
 * out on every mount.
 */
let homeFetchCache = null; // { lastFetchedCityId }

const categoryIcons = {
  'Food & Drink': foodDrinkIcon,
  'Things To Do': thingsToDoIcon,
  Shopping: shoppingIcon,
  'Self Care': selfCareIcon,
  Services: servicesIcon,
};
const targetCategoryNames = ['Food & Drink', 'Things To Do', 'Shopping', 'Self Care', 'Services'];

/**
 * Renders its children into document.body at fixed coordinates under
 * `triggerRef`, instead of `position: absolute` inside the normal DOM flow
 * — same pattern as CouponsListAll's DropdownPortal (not shared/exported
 * from anywhere, so duplicated here rather than reaching across screens).
 * Used for the hero "Category" dropdown so its open list can be fully
 * custom-styled instead of falling back to the browser/OS default list a
 * native <select> would render.
 */
const HeroDropdownPortal = ({ triggerRef, portalRef, isOpen, children }) => {
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    const updateCoords = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 6, left: rect.left, minWidth: rect.width });
    };
    updateCoords();

    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [isOpen, triggerRef]);

  if (!isOpen || !coords) return null;

  return createPortal(
    <div
      ref={portalRef}
      className={styles.dropdown}
      style={{ position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.minWidth }}
    >
      {children}
    </div>,
    document.body,
  );
};

/* ── small local carousel-arrow + header helpers (visual only) ── */
const ArrowButton = ({ dir, onClick, ariaLabel }) => (
  <button type="button" styleName={cx('arrow-btn', dir === 'left' ? 'arrow-left' : 'arrow-right')} onClick={onClick} aria-label={ariaLabel}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d={dir === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} />
    </svg>
  </button>
);

const SectionHeaderBase = ({ title, actionLabel, onAction, disabled, centered }) => (
  <div styleName={cx('title-row', { centered })}>
    <div styleName="title-with-dot">
      <span styleName="title-dot" />
      <h2 styleName="section-title">{title}</h2>
    </div>
    {onAction && (
      <button type="button" styleName={cx('see-more-btn', { 'align-right': centered })} onClick={onAction} disabled={disabled}>
        {actionLabel}
      </button>
    )}
  </div>
);
const SectionHeader = CSSModule(SectionHeaderBase, styles, { allowMultiple: true });
const ArrowBtn = CSSModule(ArrowButton, styles, { allowMultiple: true });


/* ── Favourites card, styled like the mockup's "Featured coupons" —
   full-bleed photo, dark gradient overlay, text overlaid on top —
   instead of the white-body CustomerCouponCard used elsewhere. Uses the
   same real favourite-coupon data/handlers (onPress, isFavourite,
   toggleFavourite); this is a presentational-only sibling to
   CustomerCouponCard, not a replacement for it. ── */
const FeaturedFavouriteCardBase = ({ data, onPress, isFavourite, toggleFavourite }) => {
  const {
    title,
    description,
    business: businessData,
    image_url: imageUrls,
    type,
    amount_off: amountOff,
    percentage_off: percentageOff,
    points_back: pointsBack,
  } = data;

  let dealAmount;
  if (type === COUPON_TYPES.AMOUNT_OFF) dealAmount = `$${amountOff} off`;
  else if (type === COUPON_TYPES.PERCENTAGE_OFF) dealAmount = `${Math.round(percentageOff)}% off`;
  else if (type === COUPON_TYPES.POINTS_BACK) dealAmount = `${pointsBack} points`;

  const hasImages = imageUrls && imageUrls.length > 0;
  const image = hasImages ? imageUrls[0].image_url : NoImage;
  const businessName = businessData && businessData.name;

  return (
    <button type="button" styleName="featured-card" onClick={onPress}>
      <img src={image} alt={title} styleName="featured-card-img" />
      <div styleName="featured-card-overlay" />

      {businessName && (
        <div styleName="featured-card-badge-wrap">
          <span styleName="featured-card-badge">{businessName}</span>
        </div>
      )}

      <span
        role="button"
        tabIndex={0}
        styleName="featured-card-heart"
        onClick={e => {
          e.stopPropagation();
          toggleFavourite();
        }}
        onKeyPress={e => {
          if (e.key === 'Enter') {
            e.stopPropagation();
            toggleFavourite();
          }
        }}
      >
        <Heart size={16} fill={isFavourite ? '#e0245e' : 'none'} color={isFavourite ? '#e0245e' : '#fff'} />
      </span>

      <div styleName="featured-card-text">
        <p styleName="featured-card-title">{title}</p>
        <p styleName="featured-card-subtitle">{dealAmount || description}</p>
      </div>
    </button>
  );
};
FeaturedFavouriteCardBase.propTypes = {
  data: PropTypes.object,
  onPress: PropTypes.func,
  isFavourite: PropTypes.bool,
  toggleFavourite: PropTypes.func,
};
FeaturedFavouriteCardBase.defaultProps = {
  data: {},
  onPress: undefined,
  isFavourite: false,
  toggleFavourite: undefined,
};
const FeaturedFavouriteCard = CSSModule(FeaturedFavouriteCardBase, styles, { allowMultiple: true });

/* ── Scroll-position-aware carousel hook — mirrors the useCarousel pattern
   from DealsShowcaseSection.jsx. Tracks real scrollLeft/scrollWidth so the
   left arrow only shows once you've scrolled away from the start, and the
   right arrow only shows while there's more to scroll. Purely
   presentational; no data implications. ── */
const useCarousel = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scroll = useCallback(
    dir => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({ left: dir === 'left' ? -260 : 260, behavior: 'smooth' });
      setTimeout(updateScrollState, 350);
    },
    [updateScrollState],
  );

  return { scrollRef, canScrollLeft, canScrollRight, updateScrollState, scroll };
};

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ======= Guest redirect =======
  // See file-header note. `isRestoringAuth` prevents bouncing a real
  // logged-in user to /guest-home during the brief window where
  // redux-persist is still rehydrating tokens from storage on page load.
  const authorized = useSelector(s => !!(s.auth && s.auth.authorized));
  const isRestoringAuth = useSelector(
    s => !!(s.auth && s.auth.persist && s.auth.persist.restore && s.auth.persist.restore.fetching),
  );

  useEffect(() => {
    if (isRestoringAuth) return;
    if (!authorized) {
      navigate('/', { replace: true });
    }
  }, [authorized, isRestoringAuth, navigate]);

  // ======= Redux selects =======
  const customerCouponsAllState = useSelector(s => s.customerCoupons.all || {}, shallowEqual);
  const { fetching, items, result, error } = customerCouponsAllState;
  const { results = [], nextPage } = result || {};

  const businessAll = useSelector(s => (s.businesses.all && s.businesses.all.data) || { results: [] }, shallowEqual);
  const businessList = businessAll.results || [];

  const followStateData = useSelector(s => (s.businesses.follow && s.businesses.follow.data) || { results: [] }, shallowEqual);
  const allFavourite = useSelector(s => s.customerCoupons.favourite || {}, shallowEqual);
  const appliedResults = useSelector(
    s => (s.customerCoupons.applied && s.customerCoupons.applied.data && s.customerCoupons.applied.data.results) || [],
    shallowEqual,
  );
  const followedCouponsData = useSelector(s => (s.customerCoupons.followedCoupons && s.customerCoupons.followedCoupons.data) || [], shallowEqual);

  // Restored to the real redux selectors — previously hardcoded to
  // `const activeCityId = 347;` for local debugging, which meant the hero
  // "Select Location" picker (and everything else that reads active city)
  // had no way to actually change what the feed fetched.
  const activeCityId = useSelector(s => (s.cities && s.cities.activeCityId) || null);
  const activeCity = useSelector(s => (s.cities && s.cities.activeCity) || null);

  const customerProfile = useSelector(s => (s.customerProfile && s.customerProfile.data) || {}, shallowEqual);
  const hasFreePlan = useSelector(s => !!(s.customerProfile && s.customerProfile.data && s.customerProfile.data.has_free_plan));

  const categoryState = useSelector(state => state.customerCoupons.categories || {});
  const dynamicCategories = (categoryState.data && categoryState.data.results) || categoryState.data || [];

  // ======= Local state =======
  const [paginatedGeneralCoupons, setPaginatedGeneralCoupons] = useState([]);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [isFavDeleteMode, setIsFavDeleteMode] = useState(false);
  const [isFollowDeleteMode, setIsFollowDeleteMode] = useState(false);

  // Hero search bar — location is now backed by CityPickerButtonModal (same
  // component/pattern as CouponsListAll's city chip) instead of a plain,
  // unwired text input, passed `autoConfirm` so multiple cities can be
  // picked in one sitting and each pick commits right away (popover stays
  // open between picks; the Done button just closes it). pendingCities is
  // what the modal works against; every toggle commits to selectedCities
  // immediately via handleCityConfirm — no setActiveCity dispatch (see
  // handleCityConfirm below).
  const [selectedCities, setSelectedCities] = useState([]);
  const [pendingCities, setPendingCities] = useState([]);
  const hasInitialisedCities = useRef(false);
  const [heroCategory, setHeroCategory] = useState('');
  const [isHeroCategoryOpen, setIsHeroCategoryOpen] = useState(false);
  const heroCategoryDropdownRef = useRef(null);
  const heroCategoryPortalRef = useRef(null);

  const resultsLengthRef = useRef(result?.results?.length ?? 0);

  // ======= City bootstrap =======
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CACHE_KEYS.DEFAULT_CITY);
      if (stored) {
        const parsed = JSON.parse(stored);
        dispatch(cityActions.setActiveCity(parsed.name, parsed.id));
      } else if (customerProfile?.customer_city) {
        dispatch(cityActions.setActiveCity(customerProfile.customer_city.name, customerProfile.customer_city.id));
      }
    } catch (e) {
      console.warn('Could not read default city from localStorage', e);
    }
  }, [dispatch, customerProfile?.customer_city?.id]);

  // ======= Seed the hero city picker from whatever activeCity ends up
  // being (bootstrap effect above, or a user pick from a previous visit
  // reflected back through redux). Mirrors CouponsListAll's seeding effect
  // 1:1 so the two pickers behave identically. =======
  useEffect(() => {
    if (!activeCityId || !activeCity) return;

    if (!hasInitialisedCities.current) {
      const initial = [{ id: activeCityId, name: activeCity }];
      setSelectedCities(initial);
      setPendingCities(initial);
      hasInitialisedCities.current = true;
    } else {
      setSelectedCities(prev => (prev.some(c => c.id === activeCityId) ? prev : [{ id: activeCityId, name: activeCity }]));
      setPendingCities(prev => (prev.some(c => c.id === activeCityId) ? prev : [{ id: activeCityId, name: activeCity }]));
    }
  }, [activeCityId, activeCity]);

  // This hero picker is local/display-only: it updates selectedCities (for
  // heroLocationLabel and the preSelectedCities handed off to
  // CouponsListAll) but intentionally never dispatches setActiveCity or
  // touches CACHE_KEYS.DEFAULT_CITY — the site-wide active city is only
  // ever changed via the header's picker. CityPickerButtonModal calls this
  // with the freshly-picked city array (singleSelect mode), so we use that
  // directly rather than reading pendingCities via a stale closure.
  const handleCityConfirm = useCallback(cities => {
    setSelectedCities(cities ?? []);
  }, []);

  // Shows the picked city's name once one is selected, instead of always
  // showing the static "Select Location" placeholder. Driven by
  // selectedCities (the committed pick), not pendingCities (the modal's
  // in-progress working state).
  const heroLocationLabel = selectedCities.length > 0 ? selectedCities.map(c => c.name).join(', ') : 'Select Location';

  // ======= Click outside to close the hero category dropdown =======
  useEffect(() => {
    const handleClickOutside = e => {
      const insideCategory =
        (heroCategoryDropdownRef.current && heroCategoryDropdownRef.current.contains(e.target)) ||
        (heroCategoryPortalRef.current && heroCategoryPortalRef.current.contains(e.target));
      if (!insideCategory) {
        setIsHeroCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ======= Derivations =======
  const couponList = useMemo(() => {
    if (!results || !items) {
      return [];
    }
    let list = results.map(id => items[id]).filter(Boolean);
    if (hasFreePlan) {
      list = list.filter(c => !c.is_exclusive_paid);
    }
    return list;
  }, [results, items, hasFreePlan]);

  // user-limit based blocking
  const blockedIds = useMemo(() => {
    const targets = new Set(['reserved', 'accepted', 'waiting']);
    const counts = new Map();
    for (const r of appliedResults) {
      if (!targets.has(r.state)) continue;
      const id = r.coupon;
      const limit = r?.coupon_details?.user_limit;
      const prev = counts.get(id) || { count: 0, limit };
      counts.set(id, { count: prev.count + 1, limit: prev.limit ?? limit });
    }
    const blocked = new Set();
    for (const [id, val] of counts) {
      if (typeof val.limit === 'number' && val.count >= val.limit) blocked.add(id);
    }
    return blocked;
  }, [appliedResults]);

  const isBlocked = useCallback(id => blockedIds.has(id), [blockedIds]);

  // followed ids / businesses
  const followedIds = useMemo(
    () => (followStateData?.results || []).map(x => Number(x.business_id ?? x.id)).filter(Number.isFinite),
    [followStateData],
  );
  const followedIdSet = useMemo(() => new Set(followedIds), [followedIds]);
  const followedBusinesses = useMemo(() => businessList.filter(b => followedIdSet.has(b.id)), [businessList, followedIdSet]);

  useEffect(() => {
    if (followedIds.length > 0) {
      dispatch(followedCouponsActions.getCouponsByBusinessIds(followedIds));
    }
  }, [dispatch, followedIds]);

  // add general coupons excluding followed
  useEffect(() => {
    if (!couponList.length) {
      return;
    }
    const followedSet = new Set(followedCouponsData.map(c => c.id));
    const newGeneral = couponList.filter(c => !followedSet.has(c.id));
    setPaginatedGeneralCoupons(prev => {
      const map = new Map(prev.map(c => [c.id, c]));
      for (const c of newGeneral) map.set(c.id, c);
      return Array.from(map.values());
    });
  }, [couponList, followedCouponsData]);

  // combine followed + general, dedupe, filter expired, interleave 3:1
  const interleavedCoupons = useMemo(() => {
    const general = paginatedGeneralCoupons || [];
    const followed = followedCouponsData || [];
    const now = new Date();

    const normalizeAndFilterByExpiry = list =>
      list.reduce((acc, coupon) => {
        if (coupon.expiry_type === 'date') {
          let expiryDate = coupon.expiry_date;
          // if (!expiryDate && coupon.published_at) {
          //   const published = new Date(coupon.published_at);
          //   if (!isNaN(+published)) {
          //     const tmp = new Date(published);
          //     tmp.setMonth(tmp.getMonth() + 1);
          //     expiryDate = tmp.toISOString();
          //   }
          // }
          if (expiryDate) {
            const exp = new Date(expiryDate);
            if (!isNaN(+exp) && exp < now) return acc;
          }
          acc.push(expiryDate && expiryDate !== coupon.expiry_date ? { ...coupon, expiry_date: expiryDate } : coupon);
        } else {
          acc.push(coupon);
        }
        return acc;
      }, []);

    const filteredGeneral = normalizeAndFilterByExpiry(general);
    const filteredFollowed = normalizeAndFilterByExpiry(followed);

    if (!filteredGeneral.length && !filteredFollowed.length) return [];

    const out = [];
    const BLOCK = 3;
    let gi = 0;
    let fi = 0;
    while (gi < filteredGeneral.length && fi < filteredFollowed.length) {
      const end = Math.min(gi + BLOCK, filteredGeneral.length);
      while (gi < end) out.push(filteredGeneral[gi++]);
      if (fi < filteredFollowed.length) out.push(filteredFollowed[fi++]);
    }
    while (gi < filteredGeneral.length) out.push(filteredGeneral[gi++]);
    while (fi < filteredFollowed.length) out.push(filteredFollowed[fi++]);

    return out;
  }, [paginatedGeneralCoupons, followedCouponsData]);

  // ======= Data fetchers =======
  const getAllList = useCallback(
    (params = {}) => {
      const finalParams = { page: params.page ?? 1, ...(activeCityId ? { city: activeCityId } : {}) };
      dispatch(actions.getAllList(finalParams));
    },
    [dispatch, activeCityId],
  );

  useEffect(() => {
    if (!activeCityId) {
      return;
    }
    if (homeFetchCache?.lastFetchedCityId === activeCityId) {
      // Already fetched (and possibly paginated further via scroll) for
      // this exact city earlier in this session — redux still holds that
      // data (and the "add general coupons excluding followed" effect
      // above re-derives paginatedGeneralCoupons from it automatically on
      // mount), so just reuse it instead of wiping it out and re-fetching
      // page 1.
      return;
    }
    setPaginatedGeneralCoupons([]);
    // A next-page fetch that was already in flight for the *previous* city
    // has no bearing on the city we're switching to — clear the flag so a
    // stale response landing after this point can't wedge future
    // scroll-triggered loads (see handleLoadMore's `fetching` guard below).
    setLoadingNextPage(false);
    // getAllList always sends an explicit `page` (defaulting to 1), which
    // routes every fetch — including this "fresh page 1 for the new city"
    // one — through SUCCESS_PAGINATED (`attach: true` in duck.js), i.e. it
    // merges onto whatever's already in the customerCoupons/all normalized
    // state rather than replacing it. resetCouponList is the action
    // duck.js already defines for exactly this: wipe back to
    // EXTEND_NORMALIZED_INITIAL_STATE so the incoming attach-merge has
    // nothing stale left to merge onto.
    dispatch(actions.resetCouponList());
    getAllList({ page: 1 });
    homeFetchCache = { lastFetchedCityId: activeCityId };
  }, [activeCityId, dispatch, getAllList]);

  useEffect(() => {
    dispatch(customerProfileActions.getCustomerProfile());
  }, [dispatch]);

  useEffect(() => {
    dispatch(followActions.getFollowedBusinessesRequest());
    dispatch(businessActions.getAllList({ page_size: 1000 }));
    dispatch(favCouponActions.getAllFavouriteList());
    dispatch(categoryActions.getCategoryList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(appliedActions.getApplyCouponList());
  }, [dispatch]);

  // ======= Handlers =======
  const exitAllDeleteModes = useCallback(() => {
    setIsFavDeleteMode(false);
    setIsFollowDeleteMode(false);
  }, []);

  const goToDetails = useCallback(
    coupon => {
      if (isFavDeleteMode || isFollowDeleteMode) {
        exitAllDeleteModes();
        return;
      }
      if (coupon.state === 'expired') {
        // eslint-disable-next-line no-alert
        window.alert('Coupon Expired\n\nSorry, this coupon has expired. It may be available again in the future.');
        return;
      }
      navigate(`${RouteNames.COUPONS_DETAILS}/${coupon.id}`);
    },
    [navigate, isFavDeleteMode, isFollowDeleteMode, exitAllDeleteModes],
  );

  const goToBusiness = useCallback(
    obj => {
      if (isFavDeleteMode || isFollowDeleteMode) {
        exitAllDeleteModes();
        return;
      }
      if (obj && obj.id && !obj.business) {
        navigate(`${RouteNames.BUSINESSES_DETAILS}/${obj.id}`);
        return;
      }
      if (obj?.business?.id) {
        const route = obj.is_affiliate_or_flyer ? RouteNames.AFFILIATE_BUSINESS_DETAILS : RouteNames.BUSINESSES_DETAILS;
        navigate(`${route}/${obj.business.id}`);
      }
    },
    [navigate, isFavDeleteMode, isFollowDeleteMode, exitAllDeleteModes],
  );

  const favoriteList = useMemo(() => (allFavourite?.fetched ? allFavourite?.data?.results || [] : []), [allFavourite]);

  const toggleFavouriteHandler = useCallback(
    (id, isFavourite) => {
      const action = isFavourite ? favCouponActions.removeFavourite(id) : favCouponActions.markAsFavourite(id);
      dispatchAsAsync(action, dispatch).then(() => {
        dispatch(favCouponActions.getAllFavouriteList());
      });
    },
    [dispatch],
  );

  const enterFavDeleteMode = useCallback(() => {
    setIsFavDeleteMode(true);
    setIsFollowDeleteMode(false);
  }, []);

  const enterFollowDeleteMode = useCallback(() => {
    setIsFollowDeleteMode(true);
    setIsFavDeleteMode(false);
  }, []);

  const handleRemoveFavouriteFromStrip = useCallback(item => toggleFavouriteHandler(item.id, true), [toggleFavouriteHandler]);

  const handleUnfollowBusiness = useCallback(
    business => {
      dispatch(followActions.unfollowBusinessRequest({ business_id: business.id }));
      dispatch(followActions.getFollowedBusinessesRequest());
    },
    [dispatch],
  );

  const checkFavourite = useCallback(coupon => favoriteList.some(f => f.id === coupon.id), [favoriteList]);

  // ======= Pagination (IntersectionObserver) =======
  const handleLoadMore = useCallback(() => {
    // `fetching` is included so the scroll-sentinel can't fire a "next
    // page" request while a city switch's page-1 fetch is still in
    // flight. Without this, clearing paginatedGeneralCoupons on a city
    // change shrinks the grid enough that the sentinel intersects
    // immediately, and this then re-fires with the *old* city's stale
    // `nextPage` value (redux hasn't replaced `result` with the new
    // city's data yet) — sending a "new city, old page number" request
    // that lands mixed in with the still-present old city results.
    if (!nextPage || loadingNextPage || fetching) return;
    setLoadingNextPage(true);
    getAllList({ page: nextPage });
  }, [nextPage, loadingNextPage, fetching, getAllList]);

  useEffect(() => {
    const currentLen = result?.results?.length ?? 0;
    if (loadingNextPage && currentLen > resultsLengthRef.current) {
      setLoadingNextPage(false);
    }
    resultsLengthRef.current = currentLen;
  }, [loadingNextPage, result?.results?.length]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) handleLoadMore();
      },
      { rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  const exploreCategories = useMemo(
    () =>
      targetCategoryNames
        .map(name => {
          const match = dynamicCategories.find(cat => cat.name?.trim().toLowerCase() === name.trim().toLowerCase());
          return match ? { id: match.id, icon: categoryIcons[name], name } : null;
        })
        .filter(Boolean),
    [dynamicCategories],
  );

  const isReady = !!activeCityId && exploreCategories.length > 0;

  

  const heroCategoryLabel = useMemo(() => {
    const match = exploreCategories.find(c => String(c.id) === heroCategory);
    return match ? match.name : 'Category';
  }, [exploreCategories, heroCategory]);

  const displayedCouponList = interleavedCoupons.filter(x => !isBlocked(x.id));
  const displayedFavoriteList = favoriteList.filter(x => !isBlocked(x.id));

  const isInitialLoad = fetching && !result?.results?.length;
  const isFollowedLoading = !followedBusinesses.length && !(followStateData?.results?.length === 0 && businessAll?.results?.length > 0);
  const isFavoritesLoading = !allFavourite?.fetched;

  /* ── carousels (scroll-position-aware — purely presentational,
     no data implications) ── */
  const followedCarousel = useCarousel();
  const favCarousel = useCarousel();
  const categoryCarousel = useCarousel();

  useEffect(() => {
    followedCarousel.updateScrollState();
  }, [followedBusinesses.length, followedCarousel]);

  useEffect(() => {
    favCarousel.updateScrollState();
  }, [displayedFavoriteList.length, favCarousel]);

  useEffect(() => {
    categoryCarousel.updateScrollState();
  }, [exploreCategories.length, categoryCarousel]);

  // Don't flash the full (authenticated-fetching) Home screen while we're
  // still figuring out whether the visitor is logged in, or for the one
  // tick before the redirect effect above actually navigates away.
  if (isRestoringAuth || !authorized) {
    return null;
  }

const seoTitle = 'Canadian Deals & Local Coupons | My Couponbook';
const seoDescription = "Connect with local businesses and start saving today. Find Canada's best deals, exclusive coupons, and online promo codes from your favourite brands.";

  return (
    <div styleName="page">
      <SeoHead title={seoTitle} description={seoDescription} noIndex />
      {isInitialLoad && (
        <div styleName="loading-overlay">
          <LoadingLogo size={96} label="Loading deals…" />
        </div>
      )}
      <div styleName="hero">
        <img src={CoffeeShopBG} alt="" styleName="hero-bg" />
        <div styleName="hero-overlay" />
        <div styleName="hero-content">
          <h1 styleName="hero-title">Discover deals near you</h1>
          <p styleName="hero-subtitle">Fresh coupons from the local spots you already love.</p>

          <div styleName="hero-search-bar">
            <div styleName="hero-city-picker">
              <CityPickerButtonModal
                label={heroLocationLabel}
                selectedCities={pendingCities}
                onChangeSelected={setPendingCities}
                onConfirm={handleCityConfirm}
                confirmLabel="Done"
                autoConfirm
              />
            </div>
            <div styleName="hero-category-picker" ref={heroCategoryDropdownRef}>
              <button type="button" styleName="hero-select-trigger" onClick={() => setIsHeroCategoryOpen(open => !open)}>
                <span styleName="hero-select-text">{heroCategoryLabel}</span>
                {isHeroCategoryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              <HeroDropdownPortal triggerRef={heroCategoryDropdownRef} portalRef={heroCategoryPortalRef} isOpen={isHeroCategoryOpen}>
                <button
                  type="button"
                  styleName={cx('dropdown-item', 'dropdown-item-all', { active: heroCategory === '' })}
                  onClick={() => {
                    setHeroCategory('');
                    setIsHeroCategoryOpen(false);
                  }}
                >
                  <Grid size={15} styleName="dropdown-item-feather-icon" />
                  <span>All categories</span>
                </button>
                {exploreCategories.map(item => (
                  <button
                    type="button"
                    key={item.id}
                    styleName={cx('dropdown-item', { active: heroCategory === String(item.id) })}
                    onClick={() => {
                      setHeroCategory(String(item.id));
                      setIsHeroCategoryOpen(false);
                    }}
                  >
                    <img src={item.icon} alt="" styleName="dropdown-item-icon" />
                    <span>{item.name}</span>
                  </button>
                ))}
              </HeroDropdownPortal>
            </div>
            <button
              type="button"
              styleName="hero-search-btn"
              onClick={() => {
                const navState = {};
                if (heroCategory) navState.preSelectedCategories = [Number(heroCategory)];
                if (selectedCities.length > 0) navState.preSelectedCities = selectedCities;

                if (heroCategory) {
                  dispatch(categoryActions.setPreselectedCategories([Number(heroCategory)]));
                }

                navigate(RouteNames.COUPONS_LISTS_ALL, Object.keys(navState).length > 0 ? { state: navState } : undefined);
              }}
            >
              Search
            </button>
          </div>
        </div>
      </div>
      <div styleName="wrap">
        {/* Followed Businesses */}
        <div styleName="section">
          <SectionHeader
            title="Find deals from the brands you already love."
            actionLabel={isFavDeleteMode || isFollowDeleteMode ? 'Done' : 'See All'}
            onAction={isFavDeleteMode || isFollowDeleteMode ? exitAllDeleteModes : () => navigate(RouteNames.BUSINESSES_FOLLOWED)}
            centered
          />

          {isFollowedLoading ? (
            <FollowedBusinessesSkeleton />
          ) : followedBusinesses.length > 0 ? (
            <div styleName="carousel">
              {followedCarousel.canScrollLeft && (
                <ArrowBtn dir="left" ariaLabel="Scroll followed businesses left" onClick={() => followedCarousel.scroll('left')} />
              )}
              <div
                ref={followedCarousel.scrollRef}
                onScroll={followedCarousel.updateScrollState}
                styleName={cx('h-scroll', { centered: !followedCarousel.canScrollLeft && !followedCarousel.canScrollRight })}
              >
                {followedBusinesses.map(item => (
                  <div key={item.id} styleName="business-pill">
                    <button
                      type="button"
                      styleName={isFollowDeleteMode ? 'business-pill-btn delete-mode' : 'business-pill-btn'}
                      onClick={() => (isFollowDeleteMode ? null : goToBusiness(item))}
                      onContextMenu={e => {
                        e.preventDefault();
                        enterFollowDeleteMode();
                      }}
                    >
                      <img src={item.logo} alt={item.name || ''} styleName="business-pill-img" />
                    </button>
                    {isFollowDeleteMode && (
                      <button type="button" styleName="delete-badge" onClick={() => handleUnfollowBusiness(item)} aria-label="Unfollow">
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {followedCarousel.canScrollRight && (
                <ArrowBtn dir="right" ariaLabel="Scroll followed businesses right" onClick={() => followedCarousel.scroll('right')} />
              )}
            </div>
          ) : (
            <p styleName="no-items-text">You currently don&apos;t follow any businesses</p>
          )}
        </div>

        <div styleName="separator" />

        {/* Favourites */}
        <div styleName="section">
          <SectionHeader
            title="Favourites"
            actionLabel={isFavDeleteMode || isFollowDeleteMode ? 'Done' : 'See All'}
            onAction={isFavDeleteMode || isFollowDeleteMode ? exitAllDeleteModes : () => navigate(RouteNames.FAVOURITES)}
          />

          {isFavoritesLoading ? (
            <FavoritesSkeleton />
          ) : displayedFavoriteList.length > 0 ? (
            <div styleName="carousel">
              {favCarousel.canScrollLeft && (
                <ArrowBtn dir="left" ariaLabel="Scroll favourites left" onClick={() => favCarousel.scroll('left')} />
              )}
              <div ref={favCarousel.scrollRef} onScroll={favCarousel.updateScrollState} styleName="h-scroll">
                {displayedFavoriteList.map(item => {
                  const isFavourite = checkFavourite(item);
                  return (
                    <div key={item.id} styleName="fav-pill">
                      <div styleName={isFavDeleteMode ? 'fav-pill-inner delete-mode' : 'fav-pill-inner'}>
                        <FeaturedFavouriteCard
                          data={item}
                          onPress={() => (isFavDeleteMode ? null : goToDetails(item))}
                          isFavourite={isFavourite}
                          toggleFavourite={() => toggleFavouriteHandler(item.id, isFavourite)}
                        />
                      </div>
                      {isFavDeleteMode && (
                        <button
                          type="button"
                          styleName="delete-badge"
                          onClick={() => handleRemoveFavouriteFromStrip(item)}
                          aria-label="Remove favourite"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {favCarousel.canScrollRight && (
                <ArrowBtn dir="right" ariaLabel="Scroll favourites right" onClick={() => favCarousel.scroll('right')} />
              )}
            </div>
          ) : (
            <p styleName="no-items-text">No favorite coupons yet</p>
          )}
        </div>

        <div styleName="separator" />

        {/* Explore Deals by Category */}
        <div styleName="section">
          <SectionHeader title="Explore deals by category" actionLabel="Search More" onAction={() => navigate(RouteNames.COUPONS_LISTS_ALL)} />

          <div styleName="carousel">
            {categoryCarousel.canScrollLeft && (
              <ArrowBtn dir="left" ariaLabel="Scroll categories left" onClick={() => categoryCarousel.scroll('left')} />
            )}
            <div ref={categoryCarousel.scrollRef} onScroll={categoryCarousel.updateScrollState} styleName="h-scroll">
              {exploreCategories.map(item => (
                <button
                  key={item.id}
                  type="button"
                  styleName="category-card"
                  disabled={!isReady}
                  style={{ opacity: isReady ? 1 : 0.4 }}
                  onClick={() => {
                    if (isFavDeleteMode || isFollowDeleteMode) {
                      exitAllDeleteModes();
                      return;
                    }
                    if (!isReady) return;
                    dispatch(categoryActions.setPreselectedCategories([item.id]));
                    navigate(RouteNames.COUPONS_LISTS_ALL, {
                      state: {
                        preSelectedCategories: [item.id],
                        ...(selectedCities.length > 0 ? { preSelectedCities: selectedCities } : {}),
                      },
                    });
                  }}
                >
                  <img src={item.icon} alt={item.name} styleName="category-card-img" />
                  <div styleName="category-card-overlay" />
                  <div styleName="category-card-label-wrap">
                    <p styleName="category-card-label">{item.name}</p>
                  </div>
                </button>
              ))}
            </div>
            {categoryCarousel.canScrollRight && (
              <ArrowBtn dir="right" ariaLabel="Scroll categories right" onClick={() => categoryCarousel.scroll('right')} />
            )}
          </div>
        </div>

        <div styleName="separator" />

        {/* Newest Coupons */}
        <div styleName="section">
          <SectionHeader title="Newest Coupons" actionLabel="See All" onAction={() => navigate(RouteNames.COUPONS_LISTS_ALL)} />

          {displayedCouponList.length === 0 && !isInitialLoad && <p styleName="no-items-text">No coupons available in this city.</p>}

          {isInitialLoad ? (
            <NewestCouponsSkeleton />
          ) : (
            <div styleName="coupons-grid">
              {displayedCouponList.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <TrackedCouponCard
                     item={item}
                      index={index}
                      feedId="home_newest_coupons"
                      feedSort="newest"
                      first={index === 0}
                      isFavourite={checkFavourite(item)}
                      onPress={() => goToDetails(item)}
                      goToBusinessDetails={() => goToBusiness(item)}
                      toggleFavourite={() => toggleFavouriteHandler(item.id, checkFavourite(item))}
                    />
                    {index === 8 && <SponsoredCard key="sponsored-card" />}
                  </React.Fragment>
                ))}
              <div ref={sentinelRef} styleName="scroll-sentinel" />
              {loadingNextPage && (
                <div styleName="loading-row">
                  <Spinner show size="sm" />
                  <span>Loading more coupons…</span>
                </div>
              )}
            </div>
          )}

          <p styleName="footer-note">
            To search through all coupons{' '}
            <button type="button" styleName="footer-link" onClick={() => navigate(RouteNames.COUPONS_LISTS_ALL)}>
              click here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CSSModule(HomePage, styles, { allowMultiple: true });